import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { MetricTile } from "@/components/parent/MetricTile";
import { api } from "@/lib/api";
import { format, formatDistanceToNow, parseISO, isValid } from "date-fns";
import {
  ArrowLeft,
  Calendar,
  Activity,
  Clock,
  MapPin,
  Target,
  Heart,
  Brain,
  Zap,
  Loader2,
  AlertCircle,
  ChevronRight,
} from "lucide-react";

// ─── Types ───────────────────────────────────────────────────────────────────

interface SkillMetric {
  recordDate?: string;
  [key: string]: any;
}

interface KidDashboardData {
  kid: {
    id: number;
    fullName: string;
    firstName: string;
    lastName: string;
    age: number;
    ageGroup: string;
    location: string;
  };
  attendance: {
    total: number;
    attended: number;
    missed: number;
    lastSessionDate: string | null;
    last30DaysCount: number;
  };
  upcomingSessions: Array<{
    id: number;
    title: string;
    sessionType: string;
    location: string;
    startTime: string;
    endTime: string;
    coachName: string | null;
    availabilityStatus: string | null;
  }>;
  recentSessionTitles: Array<{
    title: string;
    sessionType: string;
    startTime: string;
  }>;
  batting: SkillMetric | null;
  bowling: SkillMetric | null;
  fielding: SkillMetric | null;
  fitness: SkillMetric | null;
  discipline: SkillMetric | null;
  /** Latest assessment per discipline (player_assessments + assessment_scores).
   *  Falls back source when the legacy Postgres-only metric tables above are
   *  empty — always the case on SQLite dev. Keyed by discipline:
   *  batting | spin_bowling | pace_bowling. */
  assessments?: Record<string, { date: string; avgScore: number; level: string }>;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Average of numeric metric fields (1–5 scale). Returns null if no data. */
function avgScore(metric: SkillMetric | null, fields: string[]): number | null {
  if (!metric) return null;
  const vals = fields
    .map((f) => metric[f])
    .filter((v): v is number => typeof v === "number" && v > 0);
  if (vals.length === 0) return null;
  return vals.reduce((s, v) => s + v, 0) / vals.length;
}

/** Map a 1–5 average to a parent-friendly label. */
function scoreToLabel(score: number | null): string {
  if (score === null) return "Not assessed yet";
  if (score >= 4.5) return "Excellent";
  if (score >= 3.5) return "Strong";
  if (score >= 2.5) return "Developing";
  if (score >= 1.5) return "Building";
  return "Just starting";
}

/** "X days ago" for a record date, or null if unavailable. */
function formatLastUpdated(dateStr?: string | null): string | null {
  if (!dateStr) return null;
  try {
    const d = parseISO(dateStr);
    return isValid(d) ? formatDistanceToNow(d, { addSuffix: true }) : null;
  } catch {
    return null;
  }
}

/** Human-readable last session text. */
function formatLastSession(dateStr: string | null): string {
  if (!dateStr) return "No sessions yet";
  try {
    const d = parseISO(dateStr);
    return isValid(d) ? formatDistanceToNow(d, { addSuffix: true }) : "No sessions yet";
  } catch {
    return "No sessions yet";
  }
}

/** Weekly frequency label derived from 30-day attended count. */
function weeklyLabel(count: number): string {
  if (count === 0) return "No sessions logged";
  const perWeek = count / 4.3; // ~4.3 weeks in 30 days
  if (perWeek < 0.9) return "< 1x / week";
  if (perWeek < 1.5) return "~1x / week";
  if (perWeek < 2.5) return "~2x / week";
  if (perWeek < 3.5) return "~3x / week";
  return `~${Math.round(perWeek)}x / week`;
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function KidDashboardMVP() {
  const { kidId } = useParams<{ kidId: string }>();
  const navigate = useNavigate();

  const { data: resp, isLoading, isError, error } = useQuery({
    queryKey: [`/api/parent/kids/${kidId}/dashboard`],
    queryFn: () => api.get(`/api/parent/kids/${kidId}/dashboard`),
    enabled: !!kidId,
    staleTime: 2 * 60 * 1000,
    retry: 1,
  });

  const data: KidDashboardData | null = resp?.data ?? null;

  // ── Loading ──────────────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <div
        className="px-4 py-6 md:px-6 md:py-8 max-w-5xl mx-auto flex items-center justify-center min-h-[300px]"
        data-testid="kid-dashboard-loading"
      >
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  // ── Error ────────────────────────────────────────────────────────────────
  if (isError || !data) {
    return (
      <div
        className="px-4 py-6 md:px-6 md:py-8 max-w-5xl mx-auto"
        data-testid="kid-dashboard-error"
      >
        <Button
          variant="outline"
          onClick={() => navigate("/parent/kids")}
          className="mb-6"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to My Kids
        </Button>
        <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700">
          <AlertCircle className="h-5 w-5 mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-semibold">Could not load dashboard</p>
            <p className="text-sm mt-1">
              {(error as Error)?.message ?? "Please try again in a moment."}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ── Derived values ───────────────────────────────────────────────────────
  const { kid, attendance, upcomingSessions, recentSessionTitles, batting, bowling, fielding, fitness, discipline, assessments } = data;
  const last30Days = attendance.last30DaysCount ?? 0;

  const skills = [
    {
      id: "batting",
      name: "Batting",
      icon: Target,
      color: "text-blue-600",
      bgColor: "bg-blue-50",
      score: avgScore(batting, ["footwork", "shotSelection", "batSwingPath", "balancePosture"])
        ?? assessments?.batting?.avgScore
        ?? null,
      updatedAt: batting?.recordDate
        ?? assessments?.batting?.date
        ?? null,
    },
    {
      id: "bowling",
      name: "Bowling",
      icon: Activity,
      color: "text-green-600",
      bgColor: "bg-green-50",
      // spin_bowling / pace_bowling both roll up under "Bowling" — use whichever is more recent
      score: avgScore(bowling, ["runUpRhythm", "loadGather", "releaseConsistency", "lineLength"])
        ?? assessments?.spin_bowling?.avgScore
        ?? assessments?.pace_bowling?.avgScore
        ?? null,
      updatedAt: bowling?.recordDate
        ?? assessments?.spin_bowling?.date
        ?? assessments?.pace_bowling?.date
        ?? null,
    },
    {
      id: "fielding",
      name: "Fielding",
      icon: Zap,
      color: "text-purple-600",
      bgColor: "bg-purple-50",
      score: avgScore(fielding, ["throwingAccuracy", "catching", "groundFielding"])
        ?? assessments?.fielding?.avgScore
        ?? null,
      updatedAt: fielding?.recordDate
        ?? assessments?.fielding?.date
        ?? null,
    },
    {
      id: "fitness",
      name: "Fitness",
      icon: Heart,
      color: "text-red-600",
      bgColor: "bg-red-50",
      score: avgScore(fitness, ["runningSpeed", "endurance", "strength", "agility", "flexibility"])
        ?? assessments?.fitness?.avgScore
        ?? null,
      updatedAt: fitness?.recordDate
        ?? assessments?.fitness?.date
        ?? null,
    },
    {
      id: "mindset",
      name: "Mindset",
      icon: Brain,
      color: "text-orange-600",
      bgColor: "bg-orange-50",
      score: avgScore(discipline, ["focus", "teamwork", "coachability"]),
      updatedAt: discipline?.recordDate,
    },
  ];

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <div
      data-testid="parent-kid-dashboard-page"
      className="px-4 py-6 md:px-6 md:py-8 max-w-5xl mx-auto"
    >
      {/* Back Button */}
      <Button
        variant="outline"
        onClick={() => navigate("/parent/kids")}
        className="mb-6"
        data-testid="btn-back-to-kids"
      >
        <ArrowLeft className="h-4 w-4 mr-2" />
        Back to My Kids
      </Button>

      {/* Header */}
      <div data-testid="kid-dashboard-header" className="mb-6 md:mb-8">
        <button
          onClick={() => navigate(`/parent/kid/${kidId}`)}
          className="text-2xl md:text-3xl font-bold text-gray-900 mb-2 hover:text-blue-700 transition-colors text-left group flex items-center gap-2"
          data-testid="btn-kid-name-link"
        >
          {kid.fullName}
          <ChevronRight className="h-5 w-5 text-gray-300 group-hover:text-blue-500 shrink-0" />
        </button>
        <p className="text-sm md:text-base text-gray-600 mb-2">
          Training overview and progress.
        </p>
        <div className="flex items-center gap-2 flex-wrap">
          <Badge variant="secondary" className="text-xs">
            {kid.ageGroup}
          </Badge>
          {kid.location && (
            <span className="text-sm text-gray-500 flex items-center gap-1">
              <MapPin className="h-3.5 w-3.5" />
              {kid.location}
            </span>
          )}
        </div>
      </div>

      {/* Top Metrics Row */}
      <div
        data-testid="kid-dashboard-mvp-metrics"
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6 md:mb-8"
      >
        <MetricTile
          icon={Calendar}
          iconColor="text-blue-600"
          title="Sessions (last 30 days)"
          value={String(last30Days)}
          helper={last30Days === 0 ? "No sessions logged yet" : "Attended sessions"}
          testId="kid-metric-sessions"
          onClick={() => navigate(`/parent/kid/${kidId}`)}
        />
        <MetricTile
          icon={Activity}
          iconColor="text-green-600"
          title="Weekly training"
          value={weeklyLabel(last30Days)}
          helper="Based on last 30 days"
          testId="kid-metric-weekly"
          onClick={() => navigate(`/parent/kid/${kidId}`)}
        />
        <MetricTile
          icon={Clock}
          iconColor="text-orange-600"
          title="Last session"
          value={formatLastSession(attendance.lastSessionDate)}
          helper={
            attendance.lastSessionDate
              ? `Total attended: ${attendance.attended} session${attendance.attended !== 1 ? "s" : ""}`
              : "Sessions will appear here once logged"
          }
          testId="kid-metric-last-session"
          onClick={() => navigate(`/parent/kid/${kidId}`)}
        />
      </div>

      {/* Skills Snapshot */}
      <div className="mb-6 md:mb-8">
        <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-1">
          Skills Snapshot
        </h2>
        <p className="text-sm text-gray-500 mb-4">
          Ratings are set by coaches after each assessment session.
        </p>
        <div
          data-testid="kid-dashboard-skills"
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
        >
          {skills.map((skill) => {
            const Icon = skill.icon;
            const label = scoreToLabel(skill.score);
            const notAssessed = skill.score === null;
            const lastUpdated = formatLastUpdated(skill.updatedAt);
            return (
              <button
                key={skill.id}
                data-testid={`kid-skill-${skill.id}`}
                onClick={() => navigate(`/parent/kid/${kidId}`)}
                className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 md:p-5 text-left w-full hover:border-blue-200 hover:shadow-md transition-all cursor-pointer group"
              >
                <div className="flex items-start gap-3">
                  <div className={`p-2 rounded-lg ${skill.bgColor}`}>
                    <Icon className={`h-5 w-5 md:h-6 md:w-6 ${skill.color}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-gray-900 mb-1">
                      {skill.name}
                    </h3>
                    <p
                      className={`text-sm font-medium mb-1 ${
                        notAssessed ? "text-gray-400 italic" : "text-gray-700"
                      }`}
                    >
                      {label}
                    </p>
                    {!notAssessed && lastUpdated ? (
                      <p className="text-xs text-gray-500">
                        Updated {lastUpdated}
                      </p>
                    ) : notAssessed ? (
                      <p className="text-xs text-gray-400">
                        Pending coach assessment
                      </p>
                    ) : null}
                  </div>
                  <ChevronRight className="h-4 w-4 text-gray-300 group-hover:text-gray-500 mt-1 shrink-0 self-center ml-auto" />
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Upcoming Sessions Section */}
      <div className="mb-6 md:mb-8">
        <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4">
          Upcoming sessions
        </h2>
        {upcomingSessions.length === 0 ? (
          <div
            data-testid="kid-upcoming-empty"
            className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 md:p-5"
          >
            <p className="text-sm text-gray-500 italic">
              No upcoming sessions scheduled for this age group yet. Check back
              soon.
            </p>
          </div>
        ) : (
          <div
            data-testid="kid-dashboard-upcoming"
            className="grid grid-cols-1 md:grid-cols-2 gap-4"
          >
            {upcomingSessions.slice(0, 4).map((session) => {
              let dayLabel = "";
              let timeLabel = "";
              try {
                const start = parseISO(session.startTime);
                const end = parseISO(session.endTime);
                if (isValid(start)) dayLabel = format(start, "EEEE, MMM d");
                if (isValid(start) && isValid(end)) {
                  timeLabel = `${format(start, "h:mm a")} – ${format(end, "h:mm a")}`;
                }
              } catch {
                // ignore
              }
              return (
                <button
                  key={session.id}
                  data-testid="kid-upcoming-item"
                  onClick={() => navigate("/parent/schedule")}
                  className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 md:p-5 text-left w-full hover:border-blue-200 hover:shadow-md transition-all cursor-pointer"
                >
                  <div className="space-y-2">
                    <div className="flex items-center justify-between gap-2">
                      <Badge variant="secondary" className="text-xs font-semibold">
                        {session.sessionType ?? "Session"}
                      </Badge>
                      {session.availabilityStatus && (
                        <span
                          className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                            session.availabilityStatus === "yes"
                              ? "bg-green-100 text-green-700"
                              : session.availabilityStatus === "no"
                              ? "bg-red-100 text-red-700"
                              : "bg-yellow-100 text-yellow-700"
                          }`}
                        >
                          {session.availabilityStatus === "yes"
                            ? "Going"
                            : session.availabilityStatus === "no"
                            ? "Not going"
                            : "Maybe"}
                        </span>
                      )}
                    </div>
                    <p className="font-semibold text-gray-900">{session.title}</p>
                    {dayLabel && (
                      <p className="text-sm text-gray-700">
                        {dayLabel}
                        {timeLabel && ` · ${timeLabel}`}
                      </p>
                    )}
                    <div className="flex items-center gap-1.5 text-sm text-gray-600">
                      <MapPin className="h-3.5 w-3.5 flex-shrink-0" />
                      <span>{session.location || "TBC"}</span>
                    </div>
                    {session.coachName && (
                      <p className="text-xs text-gray-500">
                        Coach: {session.coachName}
                      </p>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Recent Sessions Section */}
      <div className="mb-6 md:mb-8">
        <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4">
          Recent sessions
        </h2>
        <div
          data-testid="kid-dashboard-drills"
          className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 md:p-5"
        >
          {recentSessionTitles.length === 0 ? (
            <p
              className="text-sm text-gray-500 italic"
              data-testid="kid-drills-empty"
            >
              No sessions logged yet. Sessions will appear here once attendance
              has been recorded by the coach.
            </p>
          ) : (
            <ul className="space-y-3">
              {recentSessionTitles.map((s, index) => {
                let dateLabel = "";
                try {
                  const d = parseISO(s.startTime);
                  if (isValid(d)) dateLabel = format(d, "MMM d");
                } catch {
                  // ignore
                }
                return (
                  <li
                    key={index}
                    data-testid="kid-drill-item"
                    className="flex items-center gap-3 text-gray-700"
                  >
                    <div className="w-2 h-2 rounded-full bg-blue-600 flex-shrink-0" />
                    <span className="text-sm md:text-base flex-1">{s.title}</span>
                    {dateLabel && (
                      <span className="text-xs text-gray-400 whitespace-nowrap">
                        {dateLabel}
                      </span>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
